// The setup wizard (LEAGUE_SETUP.md, scenario B1): a question a step, every
// one with a default already chosen, every answer previewed in a sentence
// from the same describer that renders the rules page. Creating the league
// lands on its Home with the invite widget.
//
// AGAINST THE SERVER as of 2026-09-15 (ICD-S01 `createLeague`). It used to
// call `sim.createLeague`, which made a league inside a simulator running in
// this tab: the button worked, the league appeared, the invite code looked
// real — and none of it existed anywhere else. Signing in on a phone showed
// nothing, and the link you sent your brother named no league. The whole
// screen was a rehearsal.
//
// What is sent is the ANSWERS, not the settings this screen builds for
// its previews. The server rebuilds them with the same `buildDefaultSettings`
// — so the sentence somebody reads on the last step and the rules the league
// gets come from one function — and it does so with settings a caller cannot
// hand it.
//
// It also no longer touches the simulator at all. It used to read the season
// and the draft-date default off the sim clock, which was coherent while
// creating wrote to the sim and became a fictional instant in a real
// database the moment it did not.

import { useMemo, useRef, useState } from 'react';
import {
  buildDefaultSettings,
  DEFAULT_FAAB_BUDGET,
  ELIMINATION_FAAB_BUDGET,
  describeSettings,
  divisionCounts,
  draftOrderWords,
  formatWords,
  isSlowAuction,
  leagueNameIdea,
  playsTheField,
  scoringChanges,
  scoringTable,
  setupAnswers,
  rosterSlotsOffered,
  setupPresets,
  startingPositions,
  AUCTION_DEFAULTS,
  type CreateLeagueInput,
  type ScoringConfig,
  type WaiverSystemAnswer,
  type WizardAnswers,
} from '@fsp/domain';
import type { LeagueFormatId } from '@fsp/sports-core';
import { useNavigate } from 'react-router-dom';
import { useLeagues } from '../data/LeaguesProvider';
import { createLeague, newAttemptKey } from '../data/leagues';
import { messageOf } from '../data/api';
import { periodName, useSeasonCalendar } from '../data/seasonCalendar';
import { useDelight } from '../delight/useDelight';
import { getSport, listSports } from '../sports/registry';
import { Wizard, type WizardStep } from '../templates/Wizard';
import { ScoringTableView } from './shared/ScoringTableView';
import {
  ScoringDecimals,
  ScoringEditor,
  bonusGroups,
  type ScoringTarget,
} from './shared/ScoringEditor';
import { Confirm } from './shared/Confirm';
import { RosterSlots, type RosterCounts } from './shared/RosterSlots';
import {
  Button,
  Chip,
  Chips,
  IconButton,
  Line,
  Choice,
  OneOf,
  Reveal,
  Stack,
  Text,
  TextField,
  Toggle,
  tell,
} from '../kit/ui';
import {
  HALF_HOURS,
  bidClockOptions,
  isSlowClock,
  nominationClockOptions,
  pickClockOptions,
} from './shared/clockTimes';
import '../styles/screens/setup.css';

/**
 * The two answers to the waiver question, each with the one sentence that
 * says what it means (B-135). The budget is the rules' own default, not a
 * number typed here a second time.
 *
 * The answer is stored and the clock's waiver run plays by it in a real
 * league (`waivers.process`, PLAN-2026-09-24 W6): the order these sentences
 * describe, from the standings, or the blind bids.
 */
const waiverChoices = (
  format: LeagueFormatId,
): ReadonlyArray<{ id: WaiverSystemAnswer; name: string; means: string }> => [
  {
    id: 'reverse-standings',
    name: 'Worst team picks first',
    means:
      'The waiver order follows the standings, worst record first, and resets after every regular-season week.',
  },
  {
    id: 'faab',
    name: 'Bid from a budget',
    // An elimination league's budget is bigger: a whole roster comes onto
    // waivers every week (B-364).
    means: `Every team gets $${format === 'elimination' ? ELIMINATION_FAAB_BUDGET : DEFAULT_FAAB_BUDGET} for the season and bids blind on each player; the highest bid wins.`,
  },
];

/** A draft has a round per roster spot and at most 40 rounds, so a roster holds 40. */
const MAX_ROSTER = 40;

/**
 * How the league drafts, asked in plain words (DIRECTION A6, B-433): whether
 * everyone drafts together first, each with what it means. Big sites: ESPN
 * and Yahoo lead with the live online draft and offer autopick beside it;
 * nobody leads with the jargon.
 */
const draftModeChoices = (): ReadonlyArray<{
  id: 'live' | 'autopick' | 'manual';
  name: string;
  means: string;
}> => [
  {
    id: 'live',
    name: 'Together, live',
    means: 'Everyone joins the draft room at the time and picks in turn. The best way for a group.',
  },
  {
    id: 'autopick',
    name: 'The site drafts for us',
    means:
      'Each manager ranks players beforehand, and the draft runs by itself at the time you pick.',
  },
  {
    id: 'manual',
    name: 'In person, entered after',
    means: 'Draft around a table or anywhere you like, then enter the picks here.',
  },
];

/**
 * Next Sunday evening, by the REAL clock, as a datetime-local value.
 *
 * It used to be next Sunday after the SIMULATOR's clock, which was coherent
 * while "Create league" wrote to the simulator. This screen now posts to
 * `POST /api/leagues` and the value is stored in `drafts.scheduled_at` —
 * a real instant that real people are expected to show up at. A fictional
 * clock cannot set it, and nothing downstream would ever have said so: a
 * draft scheduled in the simulator's week looks like a perfectly ordinary
 * date on every screen that renders it.
 */
/** A live draft's pick clocks, as the big sites offer them; 90 is the default. */
function defaultDraftLocal(now: number): string {
  const at = new Date(now);
  // Next Sunday, 19:00 local. Not "this Sunday": a commissioner setting a
  // league up on a Sunday afternoon does not mean tonight.
  at.setHours(19, 0, 0, 0);
  const days = (7 - at.getDay()) % 7 || 7;
  at.setDate(at.getDate() + days);
  return toLocalInput(at.getTime());
}

/**
 * The season a league created now belongs to.
 *
 * Also used to come from the simulator (`createSim` hard-codes 2026), which
 * was right by coincidence and wrong by construction. A football season is
 * named for the calendar year it starts in, and it starts in September — so
 * anything before March belongs to the previous year's season rather than
 * starting a new one in January.
 *
 * This is the client's answer and the server takes it on trust, bounded
 * 2020-2100 by the schema. ICD-S01 §14 has it as an open question: the server
 * has no calendar of its own to check it against until S-05 exists.
 */
function currentSeason(now: number): number {
  const at = new Date(now);
  return at.getMonth() < 2 ? at.getFullYear() - 1 : at.getFullYear();
}

function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A `datetime-local` value as an instant, or null while it is not a whole one. */
function toIso(local: string): string | null {
  const ms = new Date(local).getTime();
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

export function LeagueSetupScreen() {
  const navigate = useNavigate();
  const leagues = useLeagues();
  const { play } = useDelight();
  const sports = listSports();

  const [creating, setCreating] = useState(false);
  // One key for one attempt. See `data/leagues.ts`: the flag below stops a
  // second TAP, and this stops a second LEAGUE when the first request timed
  // out and was retried. It is re-minted after every definite answer, so
  // somebody who genuinely wants two leagues with the same answers gets two.
  const attempt = useRef(newAttemptKey());
  const [index, setIndex] = useState(0);
  const [sportId, setSportId] = useState(sports[0]!.id);
  const [name, setName] = useState('');
  const [teamCount, setTeamCount] = useState(10);
  const sport = getSport(sportId);
  // NAME IDEAS (B-430): an example in the empty box, in the chosen sport's
  // words, then a new idea a press, never one already offered this visit and
  // sharing no word with what the box shows. Newest first.
  const namePlaceholder = useMemo(
    () => leagueNameIdea(sport.leagueNameWords, Math.random),
    [sport.leagueNameWords],
  );
  const offeredNames = useRef<string[]>([]);
  // A SPORT THAT ASKS ITS SCORING QUESTIONS (DIRECTION A12) is answered by
  // them; one that does not is offered its presets by name, as before.
  const [scoringPresetId, setScoringPresetId] = useState(sport.scoringPresets[0]!.id);
  const questions = sport.scoringSetup?.questions ?? [];
  const [setup, setSetup] = useState<Record<string, string>>(() => setupAnswers(sport) ?? {});
  // CHANGE ANY NUMBER (PLAN-2026-10-18 §2.3): the scoring itself, from the
  // moment the commissioner opens the editor; null is the usual numbers.
  const [custom, setCustom] = useState<ScoringConfig | null>(null);
  const [target, setTarget] = useState<ScoringTarget>(null);
  const [format, setFormat] = useState<LeagueFormatId>(sport.supportedFormats[0] ?? 'h2h-points');
  // A HEAD-TO-HEAD WEEK'S SECOND GAME, against the league median (Q3, B-359):
  // asked under the format, and sent only where a week has an opponent.
  const [median, setMedian] = useState(false);
  const medianGame = median && !playsTheField(format);
  // AND A SECOND OPPONENT EVERY WEEK (Q6, B-614), Yahoo's, asked beside it.
  const [twice, setTwice] = useState(false);
  const twoOpponents = twice && !playsTheField(format);
  // THE PICK ORDER, as Sleeper asks it (B-347): Snake, Linear or Auction, and
  // under Snake a switch for the third-round reversal, which is a kind of snake.
  const [draftOrder, setDraftOrder] = useState<'snake' | 'linear' | 'auction'>('snake');
  const [reversal, setReversal] = useState(false);
  const draftType = draftOrder === 'snake' && reversal ? 'third-round-reversal' : draftOrder;
  // AN AUCTION'S CHOICES (AUCTION.md §4): Yahoo's and ESPN's $200, thirty
  // seconds to nominate and Yahoo's twenty to bid. A bid clock of hours is a
  // slow auction, with three players up at once.
  const auctioning = draftOrder === 'auction';
  const [auctionBudget, setAuctionBudget] = useState<number>(AUCTION_DEFAULTS.budget);
  const [bidSeconds, setBidSeconds] = useState<number>(AUCTION_DEFAULTS.bidSeconds);
  const [nominationSeconds, setNominationSeconds] = useState<number>(
    AUCTION_DEFAULTS.nominationSeconds,
  );
  const [lotsAtOnce, setLotsAtOnce] = useState<number>(AUCTION_DEFAULTS.slowLotsAtOnce);
  const [leftoverToWaivers, setLeftoverToWaivers] = useState(false);
  const slowAuction = auctioning && isSlowAuction(bidSeconds);
  const chooseBidClock = (seconds: number) => {
    // A SLOW AUCTION NOMINATES IN HOURS TOO, and a live one in seconds.
    if (isSlowAuction(seconds) !== isSlowAuction(bidSeconds))
      setNominationSeconds(isSlowAuction(seconds) ? 8 * 3600 : AUCTION_DEFAULTS.nominationSeconds);
    setBidSeconds(seconds);
  };
  // HOW THE LEAGUE DRAFTS (settings version 3, BACKLOG D1): by itself at the
  // time from everybody's priorities, in person and entered after, or together
  // in the draft room (D10). ASKED, NOT DEFAULTED (DIRECTION A6, B-433): for a
  // group the draft is the party, so the step waits for an answer; until one,
  // the rules read as the site's own draft.
  const [draftMode, setDraftMode] = useState<'autopick' | 'manual' | 'live' | null>(null);
  const mode = draftMode ?? 'autopick';
  // HOW EVERY MANAGER RANKS, the commissioner's to choose: it is set before
  // anybody opens their priorities, not chosen there.
  const [draftRanking, setDraftRanking] = useState<'overall' | 'by-position'>('overall');
  const [draftLocal, setDraftLocal] = useState(() => defaultDraftLocal(Date.now()));
  const [notSure, setNotSure] = useState(false);
  // THE PICK CLOCK, ASKED ONLY OF A LIVE DRAFT (B-122, B-323): an autopick
  // draft makes every pick at its instant and an offline one has no clock, so
  // for them the rule keeps its default.
  const [pickSeconds, setPickSeconds] = useState(90);
  // A SLOW DRAFT STOPS OVERNIGHT unless the commissioner says not to (C7,
  // B-341): midnight to 8 AM, MFL's hours, in the league's own zone.
  const [overnight, setOvernight] = useState<{ from: string; to: string } | null>({
    from: '00:00',
    to: '08:00',
  });
  // A SLOW DRAFT, of picks or of bids: the clock that is hours long.
  const slowClock = auctioning ? slowAuction : isSlowClock(pickSeconds);
  const overnightPause = mode === 'live' && slowClock ? overnight : null;
  // TRADES IN THE ROOM, on unless the commissioner says not to (B-345): Sleeper
  // has no switch for them at all, and a league that drafts together trades.
  const [trading, setTrading] = useState(true);
  // No trades in an auction (AUCTION.md §3.9): it has no picks to trade.
  const pickTradingAllowed = mode === 'live' && trading && !auctioning;
  const auctionAnswers = auctioning
    ? {
        budget: auctionBudget,
        nominationSeconds,
        bidSeconds,
        lotsAtOnce,
        leftoverToWaivers,
      }
    : undefined;
  // The first week, when somebody picks one. Null follows the server's
  // suggestion, which moves with the draft time.
  const [firstPeriod, setFirstPeriod] = useState<number | null>(null);
  // The worst team claims first unless the commissioner asks to bid: bidding
  // is an option, never the default.
  const [waiverSystem, setWaiverSystem] = useState<WaiverSystemAnswer>('reverse-standings');
  // A ROSTER OF ITS OWN, under the last step's More options (B-434): null is
  // the roster the scoring answers give. Kept against that roster, so
  // switching to defensive players starts again from theirs.
  const usualRosterId =
    (questions.length > 0 ? setupPresets(sport, setup)?.roster : undefined) ??
    sport.rosterPresets[0]!.id;
  const [ownRoster, setOwnRoster] = useState<{ base: string; roster: RosterCounts } | null>(null);
  const rosterAnswer =
    ownRoster !== null && ownRoster.base === usualRosterId ? ownRoster.roster : null;
  // DIVISIONS, under More options too (PLAN-2026-10-29-divisions §5): none
  // unless asked, and only as many as the team count allows, so a count the
  // league outgrew by losing teams falls back to none rather than being sent.
  const [divisionsAsked, setDivisionsAsked] = useState(0);
  const divisionChoices = divisionCounts(teamCount);
  const divisions = divisionChoices.includes(divisionsAsked) ? divisionsAsked : 0;

  const season = currentSeason(Date.now());
  const draftScheduledAt = notSure || !draftLocal ? null : toIso(draftLocal);
  // WHICH WEEKS ARE LEFT, asked of the server with the draft time about to be
  // sent — the same rule `POST /api/leagues` refuses by, so the form cannot
  // offer a week the server would turn down (PLAN-2026-09-24 item A).
  const calendar = useSeasonCalendar(sport.id, season, draftScheduledAt);
  const cal = calendar.snapshot.state === 'ready' ? calendar.snapshot.value : null;
  const startable = cal?.periods.filter((p) => p.startable) ?? [];
  const chosenFirst =
    firstPeriod !== null && startable.some((p) => p.index === firstPeriod)
      ? firstPeriod
      : (cal?.suggestedFirst ?? null);

  const answers = useMemo<WizardAnswers>(
    () => ({
      name: name.trim() || 'Untitled League',
      teamCount,
      season,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago',
      format,
      medianGame,
      twoOpponents,
      ...(questions.length > 0 ? { scoringSetup: setup } : { scoringPresetId }),
      ...(rosterAnswer === null ? {} : { roster: rosterAnswer }),
      draftType,
      draftMode: mode,
      draftScheduledAt,
      draftRanking,
      pickSeconds,
      overnightPause,
      pickTradingAllowed,
      ...(auctionAnswers === undefined ? {} : { auction: auctionAnswers }),
      ...(chosenFirst === null ? {} : { firstPeriod: chosenFirst }),
      waiverSystem,
      ...(divisions === 0 ? {} : { divisions }),
    }),
    [
      name,
      teamCount,
      season,
      format,
      medianGame,
      twoOpponents,
      questions.length,
      setup,
      scoringPresetId,
      rosterAnswer,
      draftType,
      mode,
      draftScheduledAt,
      draftRanking,
      pickSeconds,
      overnightPause,
      pickTradingAllowed,
      auctionBudget,
      nominationSeconds,
      bidSeconds,
      lotsAtOnce,
      leftoverToWaivers,
      auctioning,
      chosenFirst,
      waiverSystem,
      divisions,
    ],
  );

  // Every preview comes from the real settings the league will get — and now
  // literally so: the same answers go to the server, which builds the same
  // object with the same function. `pickSeconds` used to be assigned onto the
  // result here, which meant the preview knew about it and nothing else did.
  const doc = useMemo(() => {
    try {
      const settings = buildDefaultSettings(sport, answers);
      return { settings, doc: describeSettings(settings, sport) };
    } catch {
      return null;
    }
  }, [sport, answers]);
  const section = (id: string) => doc?.doc.sections.find((s) => s.id === id)?.sentences ?? [];

  // THE USUAL NUMBERS for these answers, and the scoring the league gets:
  // the same, or the commissioner's own once a number has changed.
  const usualPresetId = setupPresets(sport, setup)?.scoring ?? scoringPresetId;
  const usualScoring = doc?.settings.scoring ?? null;
  const customChanged =
    custom !== null && usualScoring !== null && scoringChanges(custom, usualScoring).length > 0;
  const table = useMemo(
    () =>
      doc === null
        ? null
        : scoringTable(custom ?? doc.settings.scoring, sport, {
            positions: startingPositions(doc.settings.roster, sport),
            editing: custom !== null,
            usual: { presetId: usualPresetId, config: doc.settings.scoring },
          }),
    [doc, custom, sport, usualPresetId],
  );
  // What the rules page will say once the league exists: the describer's
  // sentences for the scoring the league will actually get.
  const scoringHeading = table?.heading ?? '';
  // THE DRAFT, AS IT WILL HAPPEN: the describer says what this mode does
  // (B-122, B-267).
  const draftSentence = section('draft')[0] ?? '';

  const steps: WizardStep[] = [];

  if (sports.length > 1) {
    steps.push({
      id: 'sport',
      title: 'Which sport?',
      content: (
        <div className="fsp-choice-grid">
          {sports.map((s) => (
            <button
              key={s.id}
              type="button"
              className="fsp-choice"
              aria-pressed={s.id === sportId}
              onClick={() => setSportId(s.id)}
            >
              <span className="fsp-choice-name">{s.name}</span>
              <span className="fsp-sub">{s.proLeagueName}</span>
            </button>
          ))}
        </div>
      ),
    });
  }

  steps.push({
    id: 'name',
    title: "What's the league called?",
    hint: 'Your friends will see this on every screen.',
    content: (
      <>
        {sports.length === 1 ? (
          <Line>
            <Chip tone="accent" icon="sport">
              {sport.name}
            </Chip>
          </Line>
        ) : null}
        <Line wrap className="fsp-setup-name">
          <TextField
            label="League name"
            hideLabel
            grow
            value={name}
            onChange={setName}
            placeholder={namePlaceholder}
            maxLength={40}
            autoFocus
          />
          <Button
            icon="random"
            onClick={() => {
              const shown = name.trim() || namePlaceholder;
              const next = leagueNameIdea(sport.leagueNameWords, Math.random, [
                shown,
                ...offeredNames.current,
                namePlaceholder,
              ]);
              offeredNames.current = [next, ...offeredNames.current];
              setName(next);
            }}
          >
            Suggest one
          </Button>
        </Line>
      </>
    ),
    error:
      name.trim().length > 0 && name.trim().length < 3 ? 'Three letters or more, please.' : null,
  });

  steps.push({
    id: 'size',
    title: 'How many teams?',
    hint: 'Even numbers keep the schedule tidy. You can close seats later.',
    content: (
      <div className="fsp-setup-stepper">
        <IconButton
          icon="remove"
          label="Fewer teams"
          variant="secondary"
          size="xl"
          onClick={() => setTeamCount((n) => Math.max(4, n - 1))}
        />
        <span className="fsp-disp fsp-num fsp-setup-count">{teamCount}</span>
        <IconButton
          icon="add"
          label="More teams"
          variant="secondary"
          size="xl"
          onClick={() => setTeamCount((n) => Math.min(20, n + 1))}
        />
      </div>
    ),
    preview: doc
      ? `${teamCount} teams, ${doc.settings.playoffs.teamCount} make the playoffs.${teamCount % 2 ? ' Someone gets a bye each week.' : ''}`
      : undefined,
  });

  // THE FORMAT, A QUESTION OF ITS OWN (PLAN-2026-10-18 §2.1): it shared the
  // scoring step until 2026-10-18, two questions on one screen. Each format is
  // a card with its one line, and the chosen one opens to say what a week is,
  // who wins and what to know, for somebody who has never played it (B-429).
  steps.push({
    id: 'format',
    title: 'How does your league play?',
    hint: 'This decides who you play each week and how the champion is crowned.',
    content: (
      <Stack gap={3}>
        <div className="fsp-format-list">
          {sport.supportedFormats.map((f) => {
            const words = formatWords(f);
            const chosen = f === format;
            return (
              <div key={f} className="fsp-format" data-chosen={chosen || undefined}>
                <button
                  type="button"
                  className="fsp-format-choice"
                  aria-pressed={chosen}
                  onClick={() => {
                    setFormat(f);
                    // An elimination league's survivors bid for each roster
                    // that goes out, as the guillotine leagues do (B-364).
                    if (f === 'elimination') setWaiverSystem('faab');
                  }}
                >
                  <span className="fsp-choice-name">{words.label}</span>
                  <span className="fsp-sub fsp-ui-wrap-text">{words.tagline}</span>
                </button>
                {chosen ? (
                  <dl className="fsp-format-explainer">
                    {words.explainer.map((line) => (
                      <div key={line.label}>
                        <dt>{line.label}</dt>
                        <dd>{line.text}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </div>
            );
          })}
        </div>
        {playsTheField(format) ? null : (
          <div className="fsp-setup-question">
            <Toggle
              label="Also play the league median each week"
              on={median}
              onChange={setMedian}
            />
            <Text size="xs" tone="secondary" block>
              {median
                ? 'Two results a week: one against your opponent, and one against the league’s middle score. Score in the top half of the league for a second win, the bottom half for a second loss.'
                : 'One result a week, against your opponent. With this on, you also get a second result against the league’s middle score, so a big week against an even bigger opponent still earns a win.'}
            </Text>
          </div>
        )}
        {playsTheField(format) ? null : (
          <div className="fsp-setup-question">
            <Toggle label="Play two opponents each week" on={twice} onChange={setTwice} />
            <Text size="xs" tone="secondary" block>
              {twice
                ? 'Two games every regular-season week: your one score against each of two teams, a result in each. The playoffs are one game a round.'
                : 'One opponent a week. With this on, your score meets two teams every regular-season week, for twice the games and fewer weeks decided by one opponent’s big day.'}
            </Text>
          </div>
        )}
      </Stack>
    ),
    // The rules page's own sentences for it, the median and an elimination
    // league's final knockouts among them: said here, before it is chosen.
    preview: section('format').join(' '),
  });

  // HOW POINTS WORK (DIRECTION A12, PLAN-2026-10-18 §2.1): the sport's plain
  // questions over the usual numbers, every number in a table by position,
  // and Change any number to make the table the editor. A sport with no
  // questions is offered its presets by name.
  const canAddBonus = bonusGroups(sport);
  steps.push({
    id: 'scoring',
    title: 'How do points work?',
    hint:
      custom === null
        ? 'These are the usual numbers. Change any of them if your league plays its own way.'
        : 'Tap a number to change it. The tinted ones are not the usual.',
    wide: questions.length > 0,
    content:
      questions.length === 0 ? (
        <div className="fsp-choice-grid">
          {sport.scoringPresets.map((p) => (
            <button
              key={p.id}
              type="button"
              className="fsp-choice"
              aria-pressed={p.id === scoringPresetId}
              onClick={() => setScoringPresetId(p.id)}
            >
              <span className="fsp-choice-name">{p.name}</span>
              <span className="fsp-sub fsp-ui-wrap-text">{p.description}</span>
            </button>
          ))}
        </div>
      ) : (
        <Stack gap={3}>
          {custom === null ? (
            questions.map((q) => {
              const chosen = q.options.find((o) => o.value === setup[q.id]) ?? q.options[0]!;
              const soon = q.options.filter((o) => !o.available);
              return (
                <div key={q.id} className="fsp-setup-question">
                  {q.style === 'switch' ? (
                    <Toggle
                      label={q.label}
                      on={setup[q.id] === q.options[0]!.value}
                      onChange={(on) =>
                        setSetup((s) => ({
                          ...s,
                          [q.id]: (on ? q.options[0]! : q.options[1]!).value,
                        }))
                      }
                    />
                  ) : (
                    <OneOf
                      label={q.label}
                      value={setup[q.id] ?? q.default}
                      onChange={(v) => setSetup((s) => ({ ...s, [q.id]: v }))}
                      options={q.options.map((o) => ({
                        value: o.value,
                        label: o.available ? o.label : `${o.label} (soon)`,
                        disabled: !o.available,
                      }))}
                    />
                  )}
                  <Text size="xs" tone="secondary" block>
                    {chosen.sentence}
                  </Text>
                  {soon.map((o) => (
                    <span key={o.value} className="fsp-setup-soon">
                      Coming soon: {o.label}. {o.sentence}
                    </span>
                  ))}
                </div>
              );
            })
          ) : (
            <Line gap={2} spread wrap>
              <Text size="sm" tone="secondary">
                Starting from the usual numbers, with{' '}
                {questions
                  .map((q) => q.options.find((o) => o.value === setup[q.id])?.phrase ?? '')
                  .filter(Boolean)
                  .join(' and ')}
                .
              </Text>
              <Confirm
                label="Start over"
                confirmLabel="Back to the usual numbers?"
                tone="warn"
                busy={false}
                onConfirm={() => {
                  setCustom(null);
                  setTarget(null);
                }}
              />
            </Line>
          )}
          {custom === null ? (
            <div>
              <Button
                size="xs"
                variant="quiet"
                icon="edit"
                onClick={() => setCustom(doc?.settings.scoring ?? null)}
              >
                Change any number
              </Button>
            </div>
          ) : null}
          {table ? (
            <ScoringTableView
              table={table}
              editing={custom !== null}
              onPick={(row, s) => setTarget({ kind: 'row', row, section: s })}
              canAddBonus={canAddBonus}
              onAddBonus={(s, group) => setTarget({ kind: 'add-bonus', section: s, group })}
            />
          ) : null}
          {custom !== null ? (
            <ScoringDecimals scoring={custom} sport={sport} onChange={setCustom} />
          ) : (
            <div>
              <Button
                size="xs"
                variant="quiet"
                icon="edit"
                onClick={() => setCustom(doc?.settings.scoring ?? null)}
              >
                Change any number
              </Button>
            </div>
          )}
          {custom !== null && table ? (
            <ScoringEditor
              target={target}
              onClose={() => setTarget(null)}
              sport={sport}
              scoring={custom}
              table={table}
              onChange={setCustom}
            />
          ) : null}
        </Stack>
      ),
    preview: questions.length > 0 ? scoringHeading : section('scoring')[0],
  });

  // THE DRAFT, ASKED IN PLAIN WORDS (DIRECTION A6, B-433): together or not
  // first, as a choice with a sentence each; the time; a live draft's clocks;
  // and the order and how managers rank under More draft options. Live is
  // marked once a date is set, never chosen for anybody.
  const suggestLive = !notSure;
  steps.push({
    id: 'draft',
    title: 'How will your league draft?',
    hint:
      draftMode === null
        ? 'For a group, drafting together is half the fun.'
        : auctioning
          ? mode === 'manual'
            ? 'Hold the auction in person or anywhere you like. Afterward, you enter every sale on the league’s draft page: the player, the team and the price.'
            : mode === 'live'
              ? 'At this time everyone meets in the draft room for the auction. Anyone who is not there bids up to their own prices, else the values. Everyone is reminded a day and an hour before.'
              : 'At this time the auction runs by itself, every team bidding up to its manager’s prices. Everyone is reminded a day and an hour before.'
          : mode === 'manual'
            ? 'Draft together, in person or anywhere you like. Afterward, you enter every pick on the league’s draft page, in order, and the rosters are set when you finish.'
            : mode === 'live'
              ? 'At this time everyone drafts together in the draft room, each pick on a clock. Anyone who is not there is picked for from their queue, then their priorities. Everyone is reminded a day and an hour before.'
              : 'At this time the draft runs by itself, from each manager’s priorities. Everyone is reminded a day and an hour before.',
    content: (
      <>
        <div className="fsp-choice-grid" role="group" aria-label="How the league drafts">
          {draftModeChoices().map((c) => (
            <button
              key={c.id}
              type="button"
              className="fsp-choice"
              aria-pressed={c.id === draftMode}
              onClick={() => setDraftMode(c.id)}
            >
              <span className="fsp-choice-name">
                {c.name}{' '}
                {c.id === 'live' && suggestLive ? <Chip tone="accent">Suggested</Chip> : null}
              </span>
              <span className="fsp-sub fsp-ui-wrap-text">{c.means}</span>
            </button>
          ))}
        </div>
        <TextField
          type="datetime-local"
          label="Date and time"
          value={draftLocal}
          onChange={setDraftLocal}
          disabled={notSure}
        />
        <Chips
          label="Draft time"
          options={[{ value: 'unsure', label: 'Not sure yet' }]}
          value={notSure ? ['unsure'] : []}
          onChange={(v) => setNotSure(v.length > 0)}
        />
        {mode === 'live' ? (
          <>
            {auctioning ? (
              <>
                {/* AN AUCTION'S TWO CLOCKS (AUCTION.md §4), and a slow one's players up at once. */}
                <Choice
                  label="Time to bid"
                  searchable={false}
                  required
                  value={String(bidSeconds)}
                  onChange={(v) => v && chooseBidClock(Number(v))}
                  options={bidClockOptions()}
                />
                <Choice
                  label="Time to nominate"
                  searchable={false}
                  required
                  value={String(nominationSeconds)}
                  onChange={(v) => v && setNominationSeconds(Number(v))}
                  options={nominationClockOptions(slowAuction)}
                />
                {slowAuction ? (
                  <Choice
                    label="Players up at once"
                    searchable={false}
                    required
                    value={String(lotsAtOnce)}
                    onChange={(v) => v && setLotsAtOnce(Number(v))}
                    options={[1, 2, 3, 5].map((k) => ({ value: String(k), label: String(k) }))}
                  />
                ) : null}
                <Text size="xs" tone="secondary" block>
                  {slowAuction
                    ? 'A slow auction, over days, with several players up at once. A new high bid with less than half the bid clock left puts it back to half, so nobody wins by bidding while the other side sleeps.'
                    : 'A bid with under 10 seconds left puts the clock back to 10, so everybody can answer it.'}
                </Text>
              </>
            ) : (
              // AN EVENING'S CLOCKS AND A SLOW DRAFT'S (C7), the big sites' list.
              <Choice
                label="Time for each pick"
                searchable={false}
                required
                value={String(pickSeconds)}
                onChange={(v) => v && setPickSeconds(Number(v))}
                options={pickClockOptions()}
              />
            )}
            {slowClock ? (
              <>
                <Toggle
                  label="Stop the clock overnight"
                  on={overnight !== null}
                  onChange={(on) => setOvernight(on ? { from: '00:00', to: '08:00' } : null)}
                />
                {overnight !== null ? (
                  <Line gap={3} wrap>
                    <Choice
                      label="From"
                      searchable={false}
                      required
                      // Never the same time at both ends: that stops nothing.
                      options={HALF_HOURS.filter((t) => t.value !== overnight.to)}
                      value={overnight.from}
                      onChange={(v) => v && setOvernight({ ...overnight, from: v })}
                    />
                    <Choice
                      label="Until"
                      searchable={false}
                      required
                      options={HALF_HOURS.filter((t) => t.value !== overnight.from)}
                      value={overnight.to}
                      onChange={(v) => v && setOvernight({ ...overnight, to: v })}
                    />
                  </Line>
                ) : null}
                <Text size="xs" tone="secondary" block>
                  {auctioning ? 'A slow auction, over days.' : 'A slow draft, over days.'}{' '}
                  {overnight !== null
                    ? `In the league’s time, the clock stops for the night: ${auctioning ? 'a bid' : 'a pick'} made then still counts, and no clock runs out on somebody asleep.`
                    : 'The clock runs through the night.'}{' '}
                  League settings can change it until the draft starts.
                </Text>
              </>
            ) : null}
            {auctioning ? null : (
              <>
                <Toggle label="Trades during the draft" on={trading} onChange={setTrading} />
                <Text size="xs" tone="secondary" block>
                  {trading
                    ? 'In the draft room, managers can trade picks and players they have drafted, the same number each side. A trade happens when it is accepted, and the commissioner can undo the last one until a pick in it is made.'
                    : 'Nobody trades until the draft is over.'}{' '}
                  League settings can change it until the draft starts.
                </Text>
              </>
            )}
          </>
        ) : null}
        <Reveal label="More draft options" hook="data-more-draft">
          <OneOf
            label="Draft type"
            hideLabel
            value={draftOrder}
            onChange={setDraftOrder}
            options={[
              { value: 'snake', label: 'Snake' },
              { value: 'linear', label: 'Linear' },
              { value: 'auction', label: 'Auction' },
            ]}
          />
          {draftOrder === 'snake' ? (
            <Toggle
              label="Third-round reversal"
              description="Round 3 runs the same way as round 2, so whoever picks last in round 1 picks first in rounds 2 and 3."
              on={reversal}
              onChange={setReversal}
            />
          ) : null}
          {draftType === 'auction' ? (
            <>
              <Text size="xs" tone="secondary" block>
                Every team has the same budget. Teams take turns putting a player up, and whoever
                bids the most gets him.
              </Text>
              <Choice
                label="Budget for each team"
                searchable={false}
                required
                value={String(auctionBudget)}
                onChange={(v) => v && setAuctionBudget(Number(v))}
                options={[100, 200, 250, 300, 500, 1000].map((b) => ({
                  value: String(b),
                  label: `$${b.toLocaleString('en-US')}`,
                }))}
              />
            </>
          ) : (
            <Text size="xs" tone="secondary" block>
              {draftOrderWords(draftType).sentence}
            </Text>
          )}
          {/* A ranking is the autopick draft's (B-267): nobody ranks for a
            draft held in person. An auction's managers price players instead. */}
          {mode === 'autopick' && auctioning ? (
            <Text size="xs" tone="secondary" block>
              Each manager prices the players they want: the most they would pay. At the draft time
              every team bids up to its prices, and a player goes to whoever priced him highest, at
              $1 over the next.
            </Text>
          ) : null}
          {mode === 'autopick' && !auctioning ? (
            <>
              <OneOf
                label="How managers rank players"
                value={draftRanking}
                onChange={setDraftRanking}
                options={[
                  { value: 'overall', label: 'One list' },
                  { value: 'by-position', label: 'By position' },
                ]}
              />
              <Text size="xs" tone="secondary" block>
                {draftRanking === 'overall'
                  ? 'Each manager ranks every player in one list, with a fewest and a most at each position. Each pick is the highest player left on their list that those allow.'
                  : 'Each manager ranks a list for each position, and names a position for every round. Each pick is the highest player left on that round’s list.'}{' '}
                Everyone ranks this way, and it is set when the league is made.
              </Text>
            </>
          ) : null}
        </Reveal>
      </>
    ),
    preview:
      draftMode === null
        ? ''
        : notSure
          ? mode === 'manual'
            ? 'No draft day yet. Enter the picks whenever the league has drafted.'
            : mode === 'live'
              ? 'No draft time yet. The commissioner starts the draft from the draft room when everyone is there.'
              : 'No draft time yet. The draft does not run until one is set, which the commissioner does in League settings.'
          : draftScheduledAt !== null && Date.parse(draftScheduledAt) <= Date.now()
            ? 'That time has already passed. Pick one that is still to come.'
            : draftSentence,
    error: draftMode === null ? 'Pick how your league drafts.' : null,
  });

  // THE SEASON'S FIRST WEEK, when the sport's schedule is loaded. A league
  // made in week 3 plays week 3 to the end; the weeks already played, or that
  // kick off before the draft, are not offered.
  if (cal !== null && cal.periods.length > 0) {
    const last = cal.regularSeason.last;
    const playoffs = cal.playoffs;
    const weeks = chosenFirst === null ? 0 : last - chosenFirst + 1;
    steps.push({
      id: 'season',
      title: 'When does your season start?',
      // AGAINST THE FIRST WEEK ON OFFER, not the sport's first: picking week 5
      // before the season starts skips weeks nobody has played (review of
      // 2026-09-24).
      hint:
        chosenFirst !== null && chosenFirst > (startable[0]?.index ?? chosenFirst)
          ? 'The weeks before it will not count for your league.'
          : chosenFirst !== null && chosenFirst > cal.regularSeason.first
            ? 'The weeks before it are played or kick off before your draft, so the season starts fresh.'
            : 'The first week that kicks off after your draft.',
      content:
        startable.length === 0 ? (
          <div className="fsp-sub">
            No week of the regular season is left to start with after that draft time.
          </div>
        ) : (
          <Chips
            label="First week"
            single
            options={startable.map((p) => ({ value: String(p.index), label: p.label }))}
            value={chosenFirst === null ? [] : [String(chosenFirst)]}
            onChange={(v) => v[0] && setFirstPeriod(Number(v[0]))}
          />
        ),
      preview:
        chosenFirst === null
          ? ''
          : `${periodName(cal, chosenFirst)} to ${periodName(cal, last)}: ${weeks} week${weeks === 1 ? '' : 's'}, then the playoffs` +
            (playoffs.length > 0
              ? ` (${periodName(cal, playoffs[0]!)} to ${periodName(cal, playoffs[playoffs.length - 1]!)}).`
              : '.'),
      error:
        startable.length === 0
          ? 'Pick an earlier draft time: no week is left to start with after it.'
          : null,
    });
  }

  // WAIVERS, AFTER THE DRAFT AND THE SEASON. The draft fills the rosters;
  // this is how a team adds to one afterwards, so it is asked in that order.
  // Two cards side by side, a sentence each — the same shape as the scoring
  // presets — and the rules' own sentences under them (B-135).
  steps.push({
    id: 'waivers',
    title: 'Who gets a player two teams want?',
    hint: 'That is what waivers decide.',
    content: (
      <>
        <div className="fsp-choice-grid">
          {waiverChoices(format).map((c) => (
            <button
              key={c.id}
              type="button"
              className="fsp-choice"
              aria-pressed={c.id === waiverSystem}
              onClick={() => setWaiverSystem(c.id)}
            >
              <span className="fsp-choice-name">{c.name}</span>
              <span className="fsp-sub fsp-ui-wrap-text">{c.means}</span>
            </button>
          ))}
        </div>
        {/* AN AUCTION'S MONEY LEFT OVER, in a league that bids on waivers too
            (AUCTION.md §3.7, DraftFantasy's). */}
        {auctioning && waiverSystem === 'faab' ? (
          <>
            <Toggle
              label="Money left over from the auction joins the waiver budget"
              on={leftoverToWaivers}
              onChange={setLeftoverToWaivers}
            />
            <Text size="xs" tone="secondary" block>
              {leftoverToWaivers
                ? 'A team that saves money in the auction has that much more to bid on waivers, dollar for dollar.'
                : 'Money left over when the auction ends is gone, and every team starts waivers with the same budget.'}
            </Text>
          </>
        ) : null}
      </>
    ),
    preview: section('waivers').slice(0, 2).join(' '),
  });

  // Which answer the rules hold, read back from the settings the league will
  // get rather than from this screen's state.
  const waiverChoice = waiverChoices(format).find((c) => c.id === doc?.settings.waivers.system);

  steps.push({
    id: 'done',
    title: 'Here are your rules',
    // SAID BEFORE THE LEAGUE IS MADE (DIRECTION A17, B-431): anything here
    // changes in League settings until the draft, and what the draft is built
    // on holds after it (`fixedRules`).
    hint: 'Until the draft, League settings can change the draft time and how it runs, the roster, scoring and waivers. The number of teams and an auction are set now. From the draft on, the roster, the draft and waivers hold for the season.',
    doneLabel: creating ? 'Creating…' : 'Create league',
    content: (
      <div className="fsp-setup-rules">
        {cal !== null && chosenFirst !== null ? (
          <div>
            <div className="fsp-cap">Season</div>
            <div className="fsp-setup-rule">
              {`${periodName(cal, chosenFirst)} to ${periodName(cal, cal.regularSeason.last)}, then the playoffs.`}
            </div>
          </div>
        ) : null}
        {['league', 'scoring', 'roster', 'draft', 'waivers'].map((id) => {
          const s = doc?.doc.sections.find((x) => x.id === id);
          if (!s) return null;
          return (
            <div key={id}>
              <div className="fsp-cap">{s.title}</div>
              <div className="fsp-setup-rule">
                {(id === 'draft'
                  ? [draftSentence, ...s.sentences.slice(1, 3)]
                  : id === 'waivers' && waiverChoice
                    ? [`${waiverChoice.name}.`, ...s.sentences]
                    : // The scoring by its name, which says how many numbers
                      // changed from the usual when the commissioner changed
                      // some: the table on its own step is the whole of it.
                      id === 'scoring' && questions.length > 0
                      ? [`${scoringHeading}.`]
                      : s.sentences
                )
                  .slice(0, id === 'scoring' ? 4 : 3)
                  .join(' ')}
              </div>
            </div>
          );
        })}
        {/* MORE OPTIONS (B-434): the roster, for a league that plays its own;
            closed, so the plain questions stay the whole of the form. */}
        {doc !== null ? (
          <Reveal label="More options" hook="data-more-options">
            <div>
              <div className="fsp-cap">Roster</div>
              <Text size="xs" tone="secondary" block>
                How many players each team starts at each spot, and how many sit on the bench. The
                draft has a round for every spot.
              </Text>
            </div>
            <RosterSlots
              roster={{
                slots: doc.settings.roster.slots,
                reserve: doc.settings.roster.reserve?.count ?? 0,
              }}
              sport={sport}
              offered={rosterSlotsOffered(
                sport.rosterPresets.find((p) => p.id === usualRosterId) ?? doc.settings.roster,
                sport,
              )}
              onSlot={(slot, count) =>
                editRoster((r) => {
                  const row = r.slots.find((x) => x.slot === slot);
                  if (!row) return;
                  // AT MOST 40 SPOTS, a round of the draft each (`DraftSettings`).
                  const others = r.slots.reduce((n, x) => n + x.count, 0) - row.count;
                  row.count = Math.min(count, Math.max(0, MAX_ROSTER - others));
                })
              }
              onReserve={(count) =>
                editRoster((r) => {
                  r.reserve = count;
                })
              }
            />
            {rosterAnswer !== null ? (
              <div>
                <Button size="xs" variant="quiet" icon="undo" onClick={() => setOwnRoster(null)}>
                  Back to the usual roster
                </Button>
              </div>
            ) : null}
            <Choice
              label="Divisions"
              description={
                divisions === 0
                  ? 'The standings group by division, and division record can break ties. League settings changes them until the season starts.'
                  : `${doc.settings.schedule.divisions.map((d) => d.name).join(', ')}. The teams are split evenly between them, and League settings renames a division or moves a team until the season starts.`
              }
              searchable={false}
              required
              value={String(divisions)}
              onChange={(v) => v !== null && setDivisionsAsked(Number(v))}
              options={divisionChoices.map((n) => ({
                value: String(n),
                label: n === 0 ? 'None' : `${n} divisions`,
              }))}
            />
          </Reveal>
        ) : null}
      </div>
    ),
    error: name.trim().length < 3 ? 'Give the league a name first (step 1).' : null,
  });

  /** A change to the roster, made over what the league would get now. */
  function editRoster(
    change: (r: { slots: { slot: string; count: number }[]; reserve: number }) => void,
  ) {
    const now = doc?.settings.roster;
    if (now === undefined) return;
    const r = {
      slots: sport.slots
        .filter((sl) => sl.kind === 'starter' || sl.kind === 'bench')
        .map((sl) => ({ slot: sl.id, count: now.slots.find((x) => x.slot === sl.id)?.count ?? 0 })),
      reserve: now.reserve?.count ?? 0,
    };
    change(r);
    setOwnRoster({ base: usualRosterId, roster: r });
  }

  const create = () => {
    // Guarded because "Create league" writes, and a double-tap on a phone
    // with a slow connection would make two leagues. There is no natural key
    // to collapse two leagues on — somebody who genuinely wants two with the
    // same answers must be able to have them — so the request carries an
    // explicit `Idempotency-Key` instead, and this flag stops the second tap
    // before it becomes a second attempt at all.
    if (creating) return;
    setCreating(true);
    void (async () => {
      const body: CreateLeagueInput = {
        sport: sport.id,
        name: answers.name,
        teamCount: answers.teamCount,
        season: answers.season,
        timeZone: answers.timeZone,
        format,
        medianGame,
        ...(questions.length > 0 ? { scoringSetup: setup } : { scoringPresetId }),
        // The table itself, only when a number really changed (§4).
        ...(customChanged && custom !== null ? { scoring: custom } : {}),
        ...(rosterAnswer === null
          ? {}
          : { roster: { slots: [...rosterAnswer.slots], reserve: rosterAnswer.reserve } }),
        draftType,
        draftMode: mode,
        draftScheduledAt: answers.draftScheduledAt ?? null,
        draftRanking,
        pickSeconds,
        overnightPause,
        pickTradingAllowed,
        ...(auctionAnswers === undefined ? {} : { auction: auctionAnswers }),
        // Null asks the server, which answers by the same rule the calendar
        // did — the right thing when the schedule is not loaded here.
        firstPeriod: chosenFirst,
        waiverSystem,
        ...(divisions === 0 ? {} : { divisions }),
      };
      const reply = await createLeague(body, attempt.current);
      if (reply.status !== 'ok') {
        setCreating(false);
        // A refusal is a definite answer, so the next press is a new attempt.
        // An unreachable server is NOT: the request may have arrived, and
        // re-using the key is what makes pressing again safe.
        if (reply.status === 'refused') attempt.current = newAttemptKey();
        // Says what did NOT happen. "Something went wrong" leaves somebody
        // wondering whether to press it again and risk two leagues.
        tell(
          messageOf(
            reply,
            'That did not work. Your league was not created.',
            'Could not reach the site. Your league was not created.',
          ),
          'bad',
        );
        return;
      }
      attempt.current = newAttemptKey();
      // The hub is now short by one league, and the shell's switcher reads
      // the same list.
      leagues.reload();
      play('win');
      tell('Your league exists. Now the fun part: get your friends in.', 'good');
      navigate(`/l/${reply.data.id}`);
    })();
  };

  return (
    <Wizard
      title="Start a league"
      steps={steps}
      index={Math.min(index, steps.length - 1)}
      onIndex={setIndex}
      onDone={create}
      onCancel={() => navigate('/')}
    />
  );
}
